import React, { useState, useEffect, useCallback } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import { API_BASE, fetchCsrfToken } from './adminShared';
import './FieldTokens.css';

function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function CreateTokenModal({ show, onClose, onCreated }) {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim() || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const csrf = await fetchCsrfToken();
      const res = await fetch(`${API_BASE}/admin/field-tokens/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
        body: JSON.stringify({ label: label.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create token');
      onCreated(data); // { token, raw_token, warning }
      setLabel('');
    } catch (e2) {
      setErr(e2.message || 'Failed to create token');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">New field token</Modal.Title>
      </Modal.Header>
      <Form onSubmit={submit}>
        <Modal.Body>
          <p className="text-muted small">
            Generate a per-crew authentication token for the offline field tool.
            The token will be displayed <strong>once</strong> after creation
            &mdash; copy it immediately and deliver it to the crew through a
            secure channel.
          </p>
          <Form.Group>
            <Form.Label>Label</Form.Label>
            <Form.Control
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Wesley &mdash; Clear Creek Crew"
              maxLength={100}
              required
              disabled={busy}
              autoFocus
            />
            <Form.Text className="text-muted">
              Human-readable identifier so you can tell tokens apart in the list.
            </Form.Text>
          </Form.Group>
          {err && <Alert variant="danger" className="mt-3 mb-0">{err}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={busy || !label.trim()}>
            {busy ? 'Generating…' : 'Generate token'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function ShowOnceTokenModal({ payload, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!payload) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(payload.raw_token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // clipboard blocked (insecure origin / permissions); user can still select+copy manually
    }
  };

  return (
    <Modal show={!!payload} onHide={onClose} centered backdrop="static" size="lg">
      <Modal.Header>
        <Modal.Title className="fs-6">Token generated for &ldquo;{payload.token.label}&rdquo;</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="mb-3">
          <strong>Copy this token now.</strong> It is displayed exactly once and
          cannot be recovered. If lost, you must revoke this token and generate a new one.
        </Alert>
        <div className="ft-raw-token">
          <code>{payload.raw_token}</code>
        </div>
        <div className="d-flex align-items-center gap-2 mt-3">
          <Button variant="primary" size="sm" onClick={copy}>
            {copied ? 'Copied ✓' : 'Copy to clipboard'}
          </Button>
          <span className="text-muted small">
            Prefix: <code>{payload.token.token_prefix}</code>
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          I&rsquo;ve copied it &mdash; dismiss
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function RevokeConfirmModal({ token, onCancel, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!token) return null;

  const confirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      const csrf = await fetchCsrfToken();
      const res = await fetch(`${API_BASE}/admin/field-tokens/${token.id}/revoke/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrf },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Revoke failed');
      onConfirmed(data);
    } catch (e) {
      setErr(e.message || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={!!token} onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">Revoke token?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Revoke <strong>&ldquo;{token.label}&rdquo;</strong>
          {' '}(<code>{token.token_prefix}&hellip;</code>)?
        </p>
        <p className="text-muted small mb-0">
          The change is immediate and irreversible. The crew using this token
          will lose access on their next request. Their previously synced trees
          remain in the database.
        </p>
        {err && <Alert variant="danger" className="mt-3 mb-0">{err}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={confirm} disabled={busy}>
          {busy ? 'Revoking…' : 'Revoke token'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function FieldTokens() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [showOnce, setShowOnce] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [isSuperuser, setIsSuperuser] = useState(false);

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/field-tokens/`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTokens(data.tokens || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSuperuserFlag = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/check-auth/`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setIsSuperuser(!!(data.user && data.user.is_superuser));
      }
    } catch {
      setIsSuperuser(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
    fetchSuperuserFlag();
  }, [fetchTokens, fetchSuperuserFlag]);

  return (
    <>
      <div className="ft-header">
        <div>
          <strong>Field crew tokens</strong>
          <span className="text-muted small ms-2">
            Per-crew authentication for the offline field tool. Tokens are
            hashed at rest; the raw form is shown once at creation only.
          </span>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Button variant="outline-secondary" size="sm" onClick={fetchTokens} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
          </Button>
          {isSuperuser ? (
            <Button variant="primary" size="sm" onClick={() => setCreateModal(true)}>
              + New token
            </Button>
          ) : (
            <span
              className="text-muted small"
              title="Field token creation is restricted to superuser accounts. Ask a superuser to generate a new token; you can still revoke existing tokens below."
            >
              🔒 Superuser required to create tokens
            </span>
          )}
        </div>
      </div>

      {tokens.length === 0 && !loading ? (
        <div className="text-center py-5 text-muted">
          No field tokens yet. Click <strong>New token</strong> to generate one for a crew.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table admin-table mb-0">
            <thead>
              <tr>
                <th>Label</th>
                <th>Prefix</th>
                <th>Created by</th>
                <th>Created</th>
                <th>Last used</th>
                <th>Last IP</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} className={t.is_active ? '' : 'ft-row-revoked'}>
                  <td className="fw-medium">{t.label}</td>
                  <td><code className="small">{t.token_prefix}&hellip;</code></td>
                  <td className="small">{t.created_by_username}</td>
                  <td className="small">{formatWhen(t.created_at)}</td>
                  <td className="small">{formatWhen(t.last_used_at)}</td>
                  <td className="small font-monospace">{t.last_used_ip || '—'}</td>
                  <td>
                    {t.is_active ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="secondary" title={`Revoked ${formatWhen(t.revoked_at)} by ${t.revoked_by_username || '?'}`}>
                        Revoked
                      </Badge>
                    )}
                  </td>
                  <td>
                    {t.is_active && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => setRevoking(t)}
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateTokenModal
        show={createModal}
        onClose={() => setCreateModal(false)}
        onCreated={(payload) => {
          setCreateModal(false);
          setShowOnce(payload);
          fetchTokens();
        }}
      />
      <ShowOnceTokenModal
        payload={showOnce}
        onClose={() => setShowOnce(null)}
      />
      <RevokeConfirmModal
        token={revoking}
        onCancel={() => setRevoking(null)}
        onConfirmed={() => {
          setRevoking(null);
          fetchTokens();
        }}
      />
    </>
  );
}

export default FieldTokens;
